
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Calendar, RefreshCw } from "lucide-react";
import { useCreditsReset } from "@/hooks/useCreditsReset"; // Assuming this hook fetches nextResetDate and resetAmount (default_free_listings)
import { formatInTimeZone } from "date-fns-tz";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

interface PlanBadgeProps {
  freeListingsRemaining: number;
  purchasedListingsCredits: number;
  freeListingsUsed: number;
  totalListingsUsed?: number;
  defaultFreeListings?: number; // Added to receive this value
}

const PlanBadge = ({
  freeListingsRemaining,
  purchasedListingsCredits,
  freeListingsUsed,
  totalListingsUsed = 0,
  defaultFreeListings // This should come from useSubscription -> userCredits.default_free_listings
}: PlanBadgeProps) => {
  const { nextResetDate, resetAmount: hookResetAmount, loading: loadingResetDate, refetchResetDate } = useCreditsReset();

  // Prefer defaultFreeListings prop if available, otherwise fallback to hook's resetAmount
  const actualDefaultFreeListings = defaultFreeListings ?? hookResetAmount ?? 0;

  const totalCredits = freeListingsRemaining + purchasedListingsCredits;
  
  const percentageUsed = actualDefaultFreeListings > 0 
    ? Math.min(100, (freeListingsUsed / actualDefaultFreeListings) * 100)
    : 0;

  const formattedDate = nextResetDate 
    ? formatInTimeZone(new Date(nextResetDate), "Europe/Tirane", "dd.MM.yyyy 'në' HH:mm")
    : '';

  const tooltipMessage = nextResetDate && actualDefaultFreeListings > 0
    ? `Do të merrni ${actualDefaultFreeListings} kredite të reja falas më ${formattedDate}`
    : nextResetDate 
    ? `Data e rivendosjes: ${formattedDate}`
    : 'Data e rivendosjes së krediteve nuk është në dispozicion.';

  return (
    <div className="flex flex-col gap-3 p-4 border rounded-lg bg-background dark:bg-card">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="bg-brand-50 text-brand-500 font-medium px-2 py-1 border-brand-200 dark:bg-brand-900 dark:text-brand-300 dark:border-brand-700"
          >
            Plani: Falas
          </Badge>

          {loadingResetDate && (
            <Badge variant="secondary" className="px-2 py-1 cursor-default">
              Duke ngarkuar datën...
            </Badge>
          )}
          {!loadingResetDate && formattedDate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="px-2 py-1 cursor-default">
                    <Calendar className="w-3 h-3 mr-1" />
                    {formattedDate}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltipMessage}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
           {!loadingResetDate && !formattedDate && (
             <Badge variant="outline" className="px-2 py-1 text-xs">
                Data e rivendosjes nuk disponohet
             </Badge>
           )}
        </div>

        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={refetchResetDate}
                  disabled={loadingResetDate}
                  aria-label="Rifresko datën e rivendosjes së krediteve"
                  className="h-7 w-7"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loadingResetDate ? "animate-spin" : ""}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Rifresko datën e rivendosjes</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Badge variant="default" className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white dark:bg-green-600 dark:hover:bg-green-700">
            {totalCredits} kredite
          </Badge>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>
            Shpallje falas mujore: {freeListingsUsed} / {actualDefaultFreeListings}
          </span>
          <span>
            {freeListingsRemaining > 0
              ? `${freeListingsRemaining} falas të mbetura`
              : "Kreditet falas mujore janë përdorur"}
          </span>
        </div>
        <Progress value={percentageUsed} className="h-2" />
      </div>

      {purchasedListingsCredits > 0 && (
        <div className="text-sm text-foreground">
          Kredite të blera:{" "}
          <strong>{purchasedListingsCredits}</strong>
        </div>
      )}

      {totalListingsUsed > 0 && (
        <div className="text-sm text-muted-foreground">
          Gjithsej shpallje aktive: {totalListingsUsed}
        </div>
      )}
    </div>
  );
};

export default PlanBadge;
